import {
  Array,
  Boolean,
  Dictionary,
  InstanceOf,
  Literal,
  Null,
  Number,
  Optional,
  Record,
  Static,
  String,
  Union,
} from "runtypes";

import { EnumValues, ProjectLanguage } from "./enums.js";
import { assertNever } from "./errors.js";
import {
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  UserId,
} from "./idTypeBrands.js";
import { DATA_APP_JS_MONACO_LANG_ID } from "./dataAppTypes.js";
import { GUIDE_MARKDOWN_MONACO_LANG_ID } from "./org-guides/guideMentionUtils.js";
import { getNormalEnum } from "./runtypeEnums.js";

// Data connection details keys
export const ConnectionDetailsKeys = {
  AUTHENTICATION_TYPE_KEY: "authenticationType",
  IAM_ROLE_TRUST_POLICY_PROPERTY: "iamRoleTrustPolicy",
  DBT_SEMANTIC_LAYER_VERSION_KEY: "dbtSemanticLayerVersion",
  EXTERNAL_ID_PROPERTY: "externalId",
  OAUTH_ROLE_KEY: "oAuthRole",
  ROLE_ARN_PROPERTY: "roleArn",
  SESSION_TOKEN_PROPERTY: "sessionToken",
  USE_DBT_JDBC_DRIVER_KEY: "useDbtJdbcDriver",
};

export const SchemaRefreshQueuedReasonLiteral = Union(
  Literal("GLOBAL_RATE_LIMIT"),
  Literal("ORG_RATE_LIMIT"),
  Literal("QUEUE_EXISTS"),
  Literal("ALREADY_RUNNING"),
);
export type SchemaRefreshQueuedReason = Static<
  typeof SchemaRefreshQueuedReasonLiteral
>;
export const SchemaRefreshQueuedReason = getNormalEnum(
  SchemaRefreshQueuedReasonLiteral,
);

export const SchemaRefreshSourceLiteral = Union(
  Literal("manual"),
  Literal("scheduled"),
);
export type SchemaRefreshSource = Static<typeof SchemaRefreshSourceLiteral>;
export const SchemaRefreshSource = getNormalEnum(SchemaRefreshSourceLiteral);

export const SchemaRefreshKindLiteral = Union(
  Literal("FULL"),
  Literal("PARTIAL"),
);
export type SchemaRefreshKind = Static<typeof SchemaRefreshKindLiteral>;
export const SchemaRefreshKind = getNormalEnum(SchemaRefreshKindLiteral);

export const SchemaRefreshTriggerTypeLiteral = Union(
  Literal("USER"),
  Literal("SCHEDULED"),
  Literal("SYSTEM"),
);
export type SchemaRefreshTriggerType = Static<
  typeof SchemaRefreshTriggerTypeLiteral
>;
export const SchemaRefreshTriggerType = getNormalEnum(
  SchemaRefreshTriggerTypeLiteral,
);

// A refresh is USER-triggered when it carries a user, otherwise it's SCHEDULED.
// Never SYSTEM, so the return type is narrowed to the two values it can produce.
export function schemaRefreshTriggerTypeForUser(
  userId: UserId | null | undefined,
):
  | typeof SchemaRefreshTriggerType.USER
  | typeof SchemaRefreshTriggerType.SCHEDULED {
  return userId != null
    ? SchemaRefreshTriggerType.USER
    : SchemaRefreshTriggerType.SCHEDULED;
}

export const SchemaRefreshLifecycleStatusLiteral = Union(
  Literal("QUEUED"),
  Literal("IN_PROGRESS"),
  Literal("COMPLETED"),
  Literal("LOST"),
);
export type SchemaRefreshLifecycleStatus = Static<
  typeof SchemaRefreshLifecycleStatusLiteral
>;
export const SchemaRefreshLifecycleStatus = getNormalEnum(
  SchemaRefreshLifecycleStatusLiteral,
);

export const SchemaRefreshOutcomeLiteral = Union(
  Literal("SUCCESS"),
  Literal("PARTIAL_SUCCESS"),
  Literal("FAILURE"),
  Literal("CANCELLED"),
  Literal("SKIPPED"),
);
export type SchemaRefreshOutcome = Static<typeof SchemaRefreshOutcomeLiteral>;
export const SchemaRefreshOutcome = getNormalEnum(SchemaRefreshOutcomeLiteral);

export const SchemaRefreshErrorCategoryLiteral = Union(
  Literal("USER_ACTIONABLE"),
  Literal("TRANSIENT"),
  Literal("INTERNAL"),
  Literal("RESOURCE_LIMIT"),
  Literal("CANCELLED"),
  Literal("OAUTH_TOKEN_EXPIRED"),
);
export type SchemaRefreshErrorCategory = Static<
  typeof SchemaRefreshErrorCategoryLiteral
>;
export const SchemaRefreshErrorCategory = getNormalEnum(
  SchemaRefreshErrorCategoryLiteral,
);

export const SchemaRefreshPartialFailuresS3UploadStateLiteral = Union(
  Literal("NOT_STARTED"),
  Literal("UPLOADING"),
  Literal("UPLOADED"),
  Literal("FAILED"),
);
export type SchemaRefreshPartialFailuresS3UploadState = Static<
  typeof SchemaRefreshPartialFailuresS3UploadStateLiteral
>;
export const SchemaRefreshPartialFailuresS3UploadState = getNormalEnum(
  SchemaRefreshPartialFailuresS3UploadStateLiteral,
);

// The warehouse traversal level a DFS partial failure is attributable to.
export const SchemaRefreshPartialFailureLevelLiteral = Union(
  Literal("DATABASE"),
  Literal("SCHEMA"),
  Literal("TABLE"),
  Literal("COLUMN"),
);
export type SchemaRefreshPartialFailureLevel = Static<
  typeof SchemaRefreshPartialFailureLevelLiteral
>;
export const SchemaRefreshPartialFailureLevel = getNormalEnum(
  SchemaRefreshPartialFailureLevelLiteral,
);

export const SqlCompletionTypeLiteral = Union(
  Literal("table"),
  Literal("column"),
  Literal("schema"),
  Literal("database"),
  Literal("keyword"),
  Literal("identifier"),
);
export type SqlCompletionType = Static<typeof SqlCompletionTypeLiteral>;

// Keep in sync with DataConnectionApiType in apiTypes.ts
export const DataConnectionTypeLiteral = Union(
  Literal("athena"),
  Literal("postgres"),
  Literal("prestodb"),
  Literal("snowflake"),
  Literal("redshift"),
  Literal("bigquery"),
  Literal("sqlserver"),
  Literal("mysql"),
  Literal("mariadb"),
  Literal("spark"),
  Literal("transform"),
  Literal("trino"),
  Literal("dremio"),
  Literal("databricks"),
  Literal("clickhouse"),
  Literal("alloydb"),
  Literal("starburst"),
  Literal("materialize"),
  Literal("cloudsql"),
  Literal("cloudsql__mysql"),
  Literal("cloudsql__postgres"),
  Literal("cloudsql__sqlserver"),
  Literal("rds"),
  Literal("rds__mysql"),
  Literal("rds__postgres"),
  Literal("motherduck"),
);
export type DataConnectionType = Static<typeof DataConnectionTypeLiteral>;
export const DataConnectionType = getNormalEnum(DataConnectionTypeLiteral);

export const humanReadableDataConnectionType = (
  type: DataConnectionType,
): string => {
  switch (type) {
    case "prestodb":
      return "PrestoDB";
    case "bigquery":
      return "BigQuery";
    case "sqlserver":
      return "SQL Server";
    case "mysql":
      return "MySQL";
    case "mariadb":
      return "MariaDB";
    case "clickhouse":
      return "ClickHouse";
    case "alloydb":
      return "AlloyDB";
    case "cloudsql":
    case "cloudsql__mysql":
    case "cloudsql__postgres":
    case "cloudsql__sqlserver":
      return "Cloud SQL";
    case "rds":
    case "rds__mysql":
    case "rds__postgres":
      return "Amazon RDS";
    case "motherduck":
      return "Motherduck";
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

export const preferredConnectionTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
  DataConnectionType.postgres,
  DataConnectionType.bigquery,
  DataConnectionType.mysql,
  DataConnectionType.redshift,
  DataConnectionType.athena,
  DataConnectionType.databricks,
  DataConnectionType.clickhouse,
];

export const OAuthConnectionTypeLiteral = Union(
  Literal("bigquery"),
  Literal("databricks"),
  Literal("snowflake"),
);
export type OAuthConnectionType = Static<typeof OAuthConnectionTypeLiteral>;
export const OAuthConnectionType = getNormalEnum(OAuthConnectionTypeLiteral);

export const oAuthConnectionTypes: DataConnectionType[] = [
  DataConnectionType.bigquery,
  DataConnectionType.databricks,
  DataConnectionType.snowflake,
];

export const perUserCredsConnectionTypes: DataConnectionType[] = [
  ...oAuthConnectionTypes,
  DataConnectionType.trino,
];

export const oAuthSchemaSupportedTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
];

// Connection types that support the delta schema refresh path. The rest fall
// back to a full refresh.
export const deltaSchemaRefreshSupportedTypes: DataConnectionType[] = [
  DataConnectionType.snowflake,
];

//  Any db connection that by default has TLS/SSL enabled, without customer configuration / enablement.
//  This does not include data connections, such as sqlserver, that must "enable encryption" for SLS/TLS.
export const hasTLSEnabled = (connectionType: DataConnectionType): boolean => {
  switch (connectionType) {
    case "athena":
    case "bigquery":
    case "databricks":
    case "motherduck":
    case "redshift":
    case "snowflake":
    case "trino":
      return true;
    case "clickhouse":
    case "dremio":
    case "mariadb":
    case "mysql":
    case "postgres":
    case "prestodb":
    case "spark":
    case "sqlserver":
    case "transform":
    case "alloydb":
    case "starburst":
    case "materialize":
    case "cloudsql":
    case "cloudsql__postgres":
    case "cloudsql__mysql":
    case "cloudsql__sqlserver":
    case "rds":
      return false;
    case "rds__postgres":
    case "rds__mysql":
      return true;
    default:
      assertNever(connectionType, connectionType);
  }
};

const DataConnectionStatusLiteral = Union(
  Literal("refreshing"),
  Literal("refresh-enqueued"),
  Literal("errored"),
  Literal("fresh"),
);

export type DataConnectionStatus = Static<typeof DataConnectionStatusLiteral>;
export const DataConnectionStatus = getNormalEnum(DataConnectionStatusLiteral);

export const MONACO_SQL_LANGUAGE_TYPES = [
  "pgsql",
  "mysql",
  "redshift",
  "sql",
  "snowflake",
] as const;
// keep in sync with MonacoWebpackPlugin config in our webpack config
export type MonacoLanguageType =
  | (typeof MONACO_SQL_LANGUAGE_TYPES)[number]
  | "python"
  | "r"
  | "markdown"
  | "yaml"
  | "json"
  | "css"
  | "javascript"
  | "plaintext"
  | typeof GUIDE_MARKDOWN_MONACO_LANG_ID // doesn't need to be added to MonacoWebpackPlugin because it's a custom language
  | typeof DATA_APP_JS_MONACO_LANG_ID;

export const ProjectLanguageMonacoLanguageMap = {
  [ProjectLanguage.R]: "r" as const,
  [ProjectLanguage.PYTHON]: "python" as const,
};

export const DbtTestStatus = Union(
  Literal("pass"),
  Literal("fail"),
  Literal("warn"),
  Literal("error"),
  Literal("skipped"),
);
export type DbtTestStatus = Static<typeof DbtTestStatus>;

export const DbtTestMetadata = Record({
  name: String,
  state: DbtTestStatus,
  // column name is null when the test is across multiple columns
  columnName: String.Or(Null),
});
export type DbtTestMetadata = Static<typeof DbtTestMetadata>;

export const DbtTestMetadataList = Array(DbtTestMetadata);
export type DbtTestMetadataList = Static<typeof DbtTestMetadataList>;

export const DbtColumnMetadata = Record({
  name: String,
  description: String.Or(Null),
});

export const ProcessedDbtColumnMetadata = Record({
  dbtTests: DbtTestMetadataList.Or(Null),
  dbtDescription: String.Or(Null),
  dbtDescriptionMdStripped: String.Or(Null),
});

export type ProcessedDbtColumnMetadata = Static<
  typeof ProcessedDbtColumnMetadata
>;

export type DbtColumnMetadata = Static<typeof DbtColumnMetadata>;

export const DbtColumnMetadataList = Array(DbtColumnMetadata);
export type DbtColumnMetadataList = Static<typeof DbtColumnMetadataList>;

const DbtJobMetadata = Record({
  id: Number,
});
type DbtJobMetadata = Static<typeof DbtJobMetadata>;

export const DbtJobMetadataList = Array(DbtJobMetadata);
export type DbtJobMetadataList = Static<typeof DbtJobMetadataList>;

export const DbtMetadata = Record({
  dbtAccessUrl: String,
  dbtRunId: String.Or(Null),
  dbtAccountId: String,
  dbtJobId: String.Or(Null),
  dbtProjectId: String,
  dbtEnvironmentId: Optional(String.Or(Null)),
  database: String,
  schema: String,
  table: String,
  dbtUniqueId: String,
  dbtLastRefreshed: Union(Null, InstanceOf(Date)),
  dbtMetadataLastChecked: InstanceOf(Date),
  dbtMetadataError: Boolean,
  dbtDescription: String.Or(Null),
  dbtDescriptionMdStripped: String.Or(Null),
  dbtColumns: DbtColumnMetadataList,
});
export type DbtMetadata = Static<typeof DbtMetadata>;

export const DbtModelMetadata = DbtMetadata.extend({
  dbtTests: DbtTestMetadataList,
});
export type DbtModelMetadata = Static<typeof DbtModelMetadata>;

export const DbtSourceMetadata = DbtMetadata.extend({
  dbtFreshness: String.Or(Null),
  dbtWarnAfterCount: Number.Or(Null),
  dbtWarnAfterPeriod: String.Or(Null),
  dbtErrorAfterCount: Number.Or(Null),
  dbtErrorAfterPeriod: String.Or(Null),
});
export type DbtSourceMetadata = Static<typeof DbtSourceMetadata>;

export const MinimalDbtModelMetadata = DbtModelMetadata.pick(
  "dbtLastRefreshed",
  "dbtTests",
  "dbtMetadataLastChecked",
  "dbtRunId",
  "dbtDescription",
  "dbtDescriptionMdStripped",
  "dbtColumns",
);
export type MinimalDbtModelMetadata = Static<typeof MinimalDbtModelMetadata>;

export const MinimalDbtSourceMetadata = DbtSourceMetadata.pick(
  "dbtLastRefreshed",
  "dbtFreshness",
  "dbtMetadataLastChecked",
  "dbtWarnAfterCount",
  "dbtWarnAfterPeriod",
  "dbtErrorAfterCount",
  "dbtErrorAfterPeriod",
  "dbtRunId",
  "dbtDescription",
  "dbtDescriptionMdStripped",
  "dbtColumns",
);
export type MinimalDbtSourceMetadata = Static<typeof MinimalDbtSourceMetadata>;

export const DbtErrorResponse = Record({
  dbtMetadataError: Boolean,
});
export type DbtErrorResponse = Static<typeof DbtErrorResponse>;

export const DbtMetricTimeGrains = Array(String);
export type DbtMetricTimeGrains = Static<typeof DbtMetricTimeGrains>;

export const DbtMetricDimensions = Array(String);
export type DbtMetricDimensions = Static<typeof DbtMetricDimensions>;

export const DbtMetricDimensionV2TypeLiteral = Union(
  Literal("CATEGORICAL"),
  Literal("TIME"),
);
export type DbtMetricDimensionV2Type = Static<
  typeof DbtMetricDimensionV2TypeLiteral
>;
export const DbtMetricDimensionV2Type = getNormalEnum(
  DbtMetricDimensionV2TypeLiteral,
);

export const DbtMetricDimensionV2 = Record({
  name: String,
  description: String.Or(Null),
  label: String.Or(Null),
}).And(
  Union(
    Record({
      type: Literal("CATEGORICAL"),
      queryableGranularities: Null,
    }),
    Record({
      type: Literal("TIME"),
      queryableGranularities: Array(String),
    }),
  ),
);
export type DbtMetricDimensionV2 = Static<typeof DbtMetricDimensionV2>;

export const DbtMetric = Record({
  dbtUniqueId: String,
  name: String,
  displayName: String,
  packageName: String.Or(Null),
  dbtRunId: String,
  description: String.Or(Null),
  type: String.Or(Null),
  timeGrains: DbtMetricTimeGrains,
  dimensions: Array(DbtMetricDimensionV2),
  dbtModelName: String.Or(Null),
  dbtModelUniqueId: String,
  lastExecution: InstanceOf(Date),
  lastFetched: InstanceOf(Date),
});
export type DbtMetric = Static<typeof DbtMetric>;

export const OAuthLogicViewSettingLiteral = Union(
  Literal("DISABLE_TOKEN_SHARING"),
  Literal("ENABLE_TOKEN_SHARING"),
);
export type OAuthLogicViewSetting = Static<typeof OAuthLogicViewSettingLiteral>;
export const OAuthLogicViewSetting = getNormalEnum(
  OAuthLogicViewSettingLiteral,
);

export const OAuthPublishedAppSettingLiteral = Union(
  Literal("DISABLE_TOKEN_SHARING"),
  Literal("ENABLE_TOKEN_SHARING"),
);
export type OAuthPublishedAppSetting = Static<
  typeof OAuthPublishedAppSettingLiteral
>;
export const OAuthPublishedAppSetting = getNormalEnum(
  OAuthPublishedAppSettingLiteral,
);

export const OAuthUserScopedResultsSettingLiteral = Union(
  Literal("DISABLED"),
  Literal("ENABLED"),
);
export type OAuthUserScopedResultsSetting = Static<
  typeof OAuthUserScopedResultsSettingLiteral
>;
export const OAuthUserScopedResultsSetting = getNormalEnum(
  OAuthUserScopedResultsSettingLiteral,
);

export const DataConnectionAuthType = {
  USERNAME_AND_PASSWORD: "usernameAndPassword",
  OAUTH: "oauth",
  KEY_PAIR: "keyPair",
  CERTIFICATE: "certificate",
  SERVICE_ACCOUNT: "serviceAccount",
  ACCESS_TOKEN: "accessToken",
  IAM_ROLE: "iamRole",
};
export type DataConnectionAuthType = EnumValues<typeof DataConnectionAuthType>;

export const OAuthCredentialSettingLiteral = Union(
  Literal("WORKSPACE"),
  Literal("USER_WITH_WORKSPACE_FALLBACK"),
  Literal("USER"),
  Literal("SERVICE_ACCOUNT"),
  Literal("SSO_TOKEN"),
);
export type OAuthCredentialSetting = Static<
  typeof OAuthCredentialSettingLiteral
>;
export const OAuthCredentialSetting = getNormalEnum(
  OAuthCredentialSettingLiteral,
);

export const DataConnectionSensitivity = {
  STANDARD: "STANDARD",
  SENSITIVE: "SENSITIVE",
} as const;
export type DataConnectionSensitivity = EnumValues<
  typeof DataConnectionSensitivity
>;

/**
 * Used to determine status of a data source after a refresh and let the user
 * know if it potentially may not exist anymore. This does not determine the * status of a refresh.
 */
export const DataSourceStatusLiteral = Union(
  Literal("ORPHANED"),
  Literal("DEPRECATED"),
);
export type DataSourceStatus = Static<typeof DataSourceStatusLiteral>;
export const DataSourceStatus = getNormalEnum(DataSourceStatusLiteral);

export const DbtSemanticLayerVersionLiteral = Union(
  Literal("DBT_SEMANTIC_LAYER_V1"),
  Literal("DBT_SEMANTIC_LAYER_V2"),
);
export type DbtSemanticLayerVersion = Static<
  typeof DbtSemanticLayerVersionLiteral
>;
export const DbtSemanticLayerVersion = getNormalEnum(
  DbtSemanticLayerVersionLiteral,
);

export const DataConnectionRateLimitTypeLiteral = Union(
  DataConnectionTypeLiteral,
  Literal("default"),
  Literal("snowflake+dbt"),
);
export const DataConnectionRateLimits = Dictionary(
  Optional(Number),
  DataConnectionRateLimitTypeLiteral,
);
export type DataConnectionRateLimits = Static<typeof DataConnectionRateLimits>;

export const FilterMatchLiteral = Union(
  Literal("EXACT"),
  Literal("PREFIX"),
  Literal("REGEX"),
  Literal("SUFFIX"),
);
export type FilterMatch = Static<typeof FilterMatchLiteral>;
export const FilterMatch = getNormalEnum(FilterMatchLiteral);

export const FilterModeLiteral = Union(Literal("EXCLUDE"), Literal("INCLUDE"));
export type FilterMode = Static<typeof FilterModeLiteral>;
export const FilterMode = getNormalEnum(FilterModeLiteral);

export const StringOrRegexFilter = Record({
  match: FilterMatchLiteral,
  mode: FilterModeLiteral,
  options: Array(String),
});
export type StringOrRegexFilter = Static<typeof StringOrRegexFilter>;

/*
 * Replacing the original string[] filters with StringOrRegexFilter which encodes more information like
 * filter mode (include or exclude) and filter match (exact, prefix, suffix, regex). This information is needed
 * in order to support these mode and match options in the UI since it is not possible to differentiate between
 * a "starts with x" filter and a plain regex filter that happens to match.
 *
 * The string[] filters are now considered deprecated, but are retained to support a few existing customers
 * which had schema filtering configured prior to this change.
 */
export const SchemaFilter = Record({
  catalogFilters: Optional(Array(String)), // deprecated
  schemaFilters: Optional(Array(String)), // deprecated
  tableFilters: Optional(Array(String)), // deprecated
  columnFilters: Optional(Array(String)), // deprecated
  databaseFilter: Optional(StringOrRegexFilter),
  schemaFilter: Optional(StringOrRegexFilter),
  tableFilter: Optional(StringOrRegexFilter),
});
export type SchemaFilter = Static<typeof SchemaFilter>;

export const DataSourceGrantEntityTypeLiteral = Union(
  Literal("DATABASE"),
  Literal("SCHEMA"),
  Literal("TABLE"),
  Literal("COLUMN"),
);

export type DataSourceGrantEntityType = Static<
  typeof DataSourceGrantEntityTypeLiteral
>;
export const DataSourceGrantEntityType = getNormalEnum(
  DataSourceGrantEntityTypeLiteral,
);

export type GrantPrivileges = {
  literalPrivileges?: string[];
  privilegeGrantingPrincipals?: string[];
};
export type DataSourceGrantEntityId =
  | DataSourceTableId
  | DataSourceSchemaId
  | DataSourceDatabaseId;

export type ForceFullSyncOptions = {
  elasticsearch?: {
    enabled: boolean;
  };
};

// Type of the table in the underlying DWH. Currently used for Databricks
// metric views and Snowflake semantic views; may grow over time.
export const TableTypeLiteral = Union(
  Literal("METRIC_VIEW"),
  Literal("SEMANTIC_VIEW"),
);
export type TableType = Static<typeof TableTypeLiteral>;
export const TableType = getNormalEnum(TableTypeLiteral);

export const ColumnSemanticLayerTypeLiteral = Union(
  Literal("MEASURE"),
  Literal("DIMENSION"),
  Literal("FACT"),
);
export type ColumnSemanticLayerType = Static<
  typeof ColumnSemanticLayerTypeLiteral
>;
export const ColumnSemanticLayerType = getNormalEnum(
  ColumnSemanticLayerTypeLiteral,
);
